import { Button, IndicationUnit } from "@solomon-labs/design/components/ui";
import React, { useEffect, useRef, useState } from "react";

/**
 * @fileoverview
 * Opinionated wrapper around the UI `Button` + `IndicationUnit` primitives
 * for the wallet connect control. A consumer passes `state` and the
 * component delegates the leading indicator to `<IndicationUnit
 * variant="connect" state={…} />` and adds the button-level flash on the
 * `just-connected` transition.
 *
 * Keyframes + `@property` rules driving these animations live in
 * `packages/shared/src/styles/animations.css`.
 */

/* ── Public API ─────────────────────────────────────────── */

export type WalletConnectState =
    | "disconnected"
    | "connecting"
    | "connected"
    | "just-connected"
    | "error";

interface WalletConnectButtonProps {
    /** Current wallet lifecycle state — drives the indicator + wrapper. */
    state: WalletConnectState;
    /** Button label — caller owns the copy (e.g. truncated address, "Connecting Phantom..."). */
    label: React.ReactNode;
    /** Click handler. Ignored while state="connecting" (button is disabled). */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Button surface — defaults to `sunken` to match NavigationBar usage. */
    variant?: "raised" | "sunken" | "inverse-raised" | "transparent";
    /** Button size — defaults to `sm`. The design system's scale: it has
     *  `2xs` where the local button had `fit`. No caller passes either. */
    size?: "2xs" | "xs" | "sm" | "md" | "lg";
    /** Extra classes forwarded to the underlying Button. */
    className?: string;
    /** Show the leading indicator. Default true. Off when another control
     *  carries it, e.g. the nav's wallet box while a multisig box beside it
     *  is the active account. Only the connected states honour it (they are
     *  the only ones where a second box exists), and off also drops the
     *  just-connected flash, which is centred on the indicator. */
    indicator?: boolean;
    /** Accessible name, when the visible label alone is ambiguous. */
    "aria-label"?: string;
    /** Leading icon for the indicator-less box, standing in for the
     *  indicator (e.g. a signature icon on the nav's signer box). */
    leading?: React.ReactNode;
}

/**
 * State-driven wallet connect button. The dot animation for each state is
 * owned by `IndicationUnit` (`variant="connect"`); only the button-level
 * chrome (disabled state, flash wrapper on just-connected, hover tracking
 * on disconnected) lives here.
 *
 * State map:
 *   - `disconnected`   → IndicationUnit connect/disconnected (hover matrix)
 *   - `connecting`     → IndicationUnit connect/connecting (dot cycle);
 *                        button disabled while in flight.
 *   - `connected`      → IndicationUnit connect/connected (steady green)
 *   - `just-connected` → IndicationUnit connect/connected + radial flash
 *                        overlay on the button wrapper. Each entry into
 *                        this state (keyed internally) replays the flash.
 *   - `error`          → IndicationUnit connect/error (burst → slow)
 *
 * @version 2.1.0
 * @published 2026-04-22
 * @history
 *   2.1.0 — `indicator`, `aria-label` and `leading`, for the nav's signer
 *           box (signature icon, no indicator) beside an active multisig box.
 *   2.0.0 — Delegate indicator rendering to `IndicationUnit variant="connect"`
 *           (keyframes moved to animations.css in the prior commit). Button
 *           now only owns hover tracking + the just-connected flash overlay.
 *   1.0.0 — Initial publish.
 */
export function WalletConnectButton({
    state,
    label,
    onClick,
    variant = "sunken",
    size = "sm",
    className,
    indicator = true,
    "aria-label": ariaLabel,
    leading
}: WalletConnectButtonProps): React.ReactElement {
    // Force a fresh mount each time we enter "just-connected" so the flash
    // animation re-plays on every successful connect (not only the first).
    const [flashKey, setFlashKey] = useState(0);
    const wasJustConnected = useRef(false);
    useEffect(() => {
        if (state === "just-connected" && !wasJustConnected.current) {
            setFlashKey((k) => k + 1);
        }
        wasJustConnected.current = state === "just-connected";
    }, [state]);

    // Track hover locally for the disconnected state's matrix rain.
    const [hovered, setHovered] = useState(false);
    const handleEnter = (): void => setHovered(true);
    const handleLeave = (): void => setHovered(false);

    if (state === "connecting") {
        return (
            <Button
                variant={variant}
                size={size}
                interaction="lifted"
                outline
                disabled
                aria-label={ariaLabel}
                leading={
                    <IndicationUnit variant="connect" state="connecting" />
                }
                className={className}
            >
                {label}
            </Button>
        );
    }

    if (state === "error") {
        return (
            <Button
                variant={variant}
                size={size}
                interaction="lifted"
                outline
                onClick={onClick}
                aria-label={ariaLabel}
                leading={<IndicationUnit variant="connect" state="error" />}
                className={className}
            >
                {label}
            </Button>
        );
    }

    if (!indicator && (state === "connected" || state === "just-connected")) {
        return (
            <Button
                variant={variant}
                size={size}
                interaction="lifted"
                outline
                onClick={onClick}
                aria-label={ariaLabel}
                className={className}
                leading={leading}
            >
                {label}
            </Button>
        );
    }

    if (state === "just-connected") {
        return (
            <div key={flashKey} className="relative inline-flex w-fit">
                <Button
                    variant={variant}
                    size={size}
                    interaction="lifted"
                    outline
                    onClick={onClick}
                    aria-label={ariaLabel}
                    leading={
                        <IndicationUnit variant="connect" state="connected" />
                    }
                    className={className}
                >
                    {label}
                </Button>
                <span
                    className="pointer-events-none absolute inset-0 z-10"
                    style={{
                        backgroundColor: "rgb(var(--color-success-base))",
                        WebkitMaskImage:
                            "radial-gradient(circle at 23px 50%, transparent var(--ring-inner), black var(--ring-inner), black var(--ring-outer), transparent var(--ring-outer))",
                        maskImage:
                            "radial-gradient(circle at 23px 50%, transparent var(--ring-inner), black var(--ring-inner), black var(--ring-outer), transparent var(--ring-outer))",
                        animation: "wallet-flash 1500ms ease-out forwards"
                    }}
                />
            </div>
        );
    }

    if (state === "connected") {
        return (
            <Button
                variant={variant}
                size={size}
                interaction="lifted"
                outline
                onClick={onClick}
                aria-label={ariaLabel}
                leading={<IndicationUnit variant="connect" state="connected" />}
                className={className}
            >
                {label}
            </Button>
        );
    }

    // Disconnected (default) — wrap in a div so we can track hover without
    // requiring Button to expose onMouseEnter/Leave.
    return (
        <div
            className="inline-flex w-fit"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            <Button
                variant={variant}
                size={size}
                interaction="lifted"
                outline
                onClick={onClick}
                aria-label={ariaLabel}
                leading={
                    <IndicationUnit
                        variant="connect"
                        state="disconnected"
                        hovered={hovered}
                    />
                }
                className={className}
            >
                {label}
            </Button>
        </div>
    );
}
